import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { Badge } from '@capawesome/capacitor-badge';
import { App } from '@capacitor/app';
import axiosInstance from '../config/axiosInstance';
import { toast } from 'react-hot-toast';
import { resolveNotificationRoute, PushNotificationData } from '../utils/notificationDeepLink.util';
import userService from './user.service';

/**
 * Service de gestion des Push Notifications
 * Gère l'enregistrement, les permissions et la réception des notifications
 */
class PushNotificationService {
  private token: string | null = null;
  private listenersRegistered = false;

  /**
   * Initialise le service de push notifications
   * À appeler au démarrage de l'application
   */
  async initialize(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      this.setupListeners();
      await this.requestPermission();
      await this.register();
    } catch (error) {
      console.error('Erreur initialisation push notifications:', error);
      throw error;
    }
  }

  /**
   * Demande la permission d'envoyer des notifications
   * Sur iOS: popup système
   * Sur Android: auto-accordée (avant Android 13)
   */
  private async requestPermission(): Promise<void> {
    const result = await PushNotifications.requestPermissions();

    if (result.receive !== 'granted') {
      throw new Error('Permission refusée par l\'utilisateur');
    }
  }

  /**
   * Enregistre l'appareil pour recevoir des notifications
   * Génère un token unique pour cet appareil
   */
  private async register(): Promise<void> {
    await PushNotifications.register();
  }

  /**
   * Configure tous les listeners d'événements
   */
  private setupListeners(): void {
    // Idempotent : les 5 listeners ne sont attachés qu'une seule fois pour la
    // durée de vie du singleton. Sans ce garde, chaque ré-initialisation (toggle
    // « activer les notifications » dans Paramètres, retry après erreur) empilait
    // un nouveau jeu de listeners → toasts et navigations dupliqués à la réception.
    if (this.listenersRegistered) {
      return;
    }
    this.listenersRegistered = true;

    PushNotifications.addListener('registration', async (token: Token) => {
      console.log('📱 Token reçu (Capacitor):', token.value);
      this.token = token.value;
      await this.sendTokenToBackend(token.value);
    });

    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('❌ Erreur enregistrement push:', error);
      toast.error('Impossible d\'activer les notifications');
    });

    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        console.log('📬 Notification reçue (app ouverte):', notification);
        this.handleNotificationReceived(notification);
        // Notification reçue app au premier plan : iOS ne met pas toujours à jour
        // le badge tout seul dans ce cas (contrairement à une notif reçue en fond,
        // où l'OS applique directement `aps.badge` du payload) — resynchronise.
        void this.syncBadge();
      }
    );

    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        console.log('👆 Notification cliquée:', action);
        this.handleNotificationClick(action);
      }
    );

    // Retour au premier plan (depuis l'arrière-plan ou le multitâche) : le badge
    // affiché par l'OS peut être périmé si l'utilisateur a lu des notifications
    // ailleurs (autre appareil, notifications système) sans qu'aucun nouveau push
    // (donc aucun `aps.badge` à jour) n'ait été reçu entre-temps.
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        void this.syncBadge();
      }
    });

    console.log('✅ Listeners configurés avec succès');
  }

  /**
   * Indique si l'utilisateur est authentifié (présence du token API).
   * On s'appuie sur la même source que l'intercepteur axios (`api_token`).
   */
  private isAuthenticated(): boolean {
    return !!localStorage.getItem('api_token');
  }

  /**
   * Envoie le token au backend Laravel
   * @param token - Token push (APNs ou FCM)
   */
  private async sendTokenToBackend(token: string): Promise<void> {
    // Si on n'est pas encore authentifié, l'appel échouerait en 401 (et serait
    // possiblement interrompu par une navigation). On conserve le token et on
    // l'enverra dès que `momentum:auth-login` sera dispatché ou à la prochaine init.
    if (!this.isAuthenticated()) {
      localStorage.setItem('push_token_pending', token);
      return;
    }

    try {
      const platform = Capacitor.getPlatform();

      const response = await axiosInstance.post('/device-tokens', {
        token: token,
        platform: platform,
        device_model: await this.getDeviceModel(),
        os_version: await this.getOSVersion(),
        app_version: await this.getAppVersion(),
      });

      localStorage.setItem('push_token', token);
      // Succès : plus rien en attente pour ce token.
      localStorage.removeItem('push_token_pending');
    } catch (error: any) {
      console.error('Erreur envoi token:', error.response?.status || error.message);

      // 401 (pas/plus authentifié) ou toute autre erreur (réseau, navigation en
      // cours...) : on conserve le token pour réessayer après login / prochaine init.
      localStorage.setItem('push_token_pending', token);
    }
  }

  /**
   * Gère la réception d'une notification quand l'app est ouverte
   * @param notification - Données de la notification
   */
  private handleNotificationReceived(notification: PushNotificationSchema): void {
    // Afficher un toast avec le contenu de la notification
    const message = `${notification.title}\n${notification.body}`;
    toast.success(message, {
      duration: 5000,
      position: 'top-right',
    });
  }

  /**
   * Gère le clic sur une notification
   * Navigation vers le contenu approprié
   * @param action - Action performée
   */
  private handleNotificationClick(action: ActionPerformed): void {
    // Construit la route cible à partir du payload (data.type + IDs d'entité).
    const data = action.notification?.data as PushNotificationData | undefined;
    const route = resolveNotificationRoute(data);

    localStorage.setItem('pending_notification_navigation', route);

    // Conserve le payload pour que l'écran cible puisse affiner le contexte si besoin.
    if (data) {
      try {
        localStorage.setItem('pending_notification_data', JSON.stringify(data));
      } catch {
        /* payload non sérialisable : on ignore */
      }
    }
  }

  /**
   * Récupère le modèle de l'appareil
   */
  private async getDeviceModel(): Promise<string> {
    try {
      // Peut être amélioré avec @capacitor/device
      const platform = Capacitor.getPlatform();
      return platform === 'ios' ? 'iPhone' : 'Android Device';
    } catch {
      return 'Unknown';
    }
  }

  /**
   * Récupère la version de l'OS
   */
  private async getOSVersion(): Promise<string> {
    try {
      // Peut être amélioré avec @capacitor/device
      return 'Unknown';
    } catch {
      return 'Unknown';
    }
  }

  /**
   * Récupère la version de l'app
   */
  private async getAppVersion(): Promise<string> {
    try {
      // Peut être amélioré avec @capacitor/app
      return '1.0.0';
    } catch {
      return '1.0.0';
    }
  }

  /**
   * Supprime le token de l'appareil (lors de la déconnexion)
   */
  async removeToken(): Promise<void> {
    // Toujours vider le badge à la déconnexion/désactivation, même sans token à
    // supprimer côté back : sinon le badge reste affiché avec un compteur qui
    // n'est plus le sien (utilisateur déconnecté ou push désactivé = plus aucune
    // notif ne le mettra à jour).
    if (Capacitor.isNativePlatform()) {
      try {
        await Badge.clear();
      } catch (error) {
        console.error('Erreur suppression badge:', error);
      }
    }

    const token = this.token || localStorage.getItem('push_token');

    if (!token) {
      return;
    }

    try {
      await axiosInstance.delete(`/device-tokens/${token}`);
      localStorage.removeItem('push_token');
      localStorage.removeItem('push_token_pending');
      this.token = null;
    } catch (error) {
      console.error('Erreur suppression token:', error);
    }
  }

  /**
   * Envoie un token en attente après connexion.
   *
   * Robuste : ne tente l'envoi que si un token est en attente ET que l'on est
   * authentifié. En cas d'échec, `sendTokenToBackend` reconserve le token en
   * attente (pas de suppression aveugle ici), de sorte qu'un prochain
   * `momentum:auth-login` ou une prochaine init puisse réessayer.
   */
  async sendPendingToken(): Promise<void> {
    const pendingToken = localStorage.getItem('push_token_pending');

    if (!pendingToken) {
      return;
    }

    if (!this.isAuthenticated()) {
      // On garde le token : il partira une fois `api_token` présent.
      return;
    }

    // La suppression du token en attente est gérée dans `sendTokenToBackend`
    // (uniquement en cas de succès), pour éviter de le perdre si l'envoi échoue.
    await this.sendTokenToBackend(pendingToken);
  }

  /**
   * Récupère les notifications en attente (badge)
   */
  async getPendingNotifications(): Promise<void> {
    try {
      await PushNotifications.getDeliveredNotifications();
    } catch (error) {
      console.error('Erreur récupération notifications:', error);
    }
  }

  /**
   * Synchronise le badge de l'icône de l'app avec le nombre réel de notifications
   * non montrées (Push iOS #MOM-465).
   *
   * Le back inclut déjà `aps.badge` (compteur exact) dans chaque push APNs envoyé
   * (cf. PushNotificationService::sendToUser côté back) : l'OS met alors le badge à
   * jour tout seul quand l'app est en arrière-plan/fermée, sans rien à faire ici.
   * Cette méthode couvre les cas que l'OS ne couvre pas seul : notification reçue
   * app au premier plan, retour au premier plan après lecture de notifications
   * ailleurs (in-app, autre appareil) sans nouveau push entre-temps.
   *
   * Best-effort et silencieux : jamais bloquant pour le reste de l'app, jamais de
   * toast d'erreur pour un badge (détail non critique). No-op hors plateforme
   * native ou si `@capawesome/capacitor-badge` n'est pas disponible.
   */
  async syncBadge(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      const count = await userService.getNotShownNotificationCount();
      if (count > 0) {
        await Badge.set({ count });
      } else {
        await Badge.clear();
      }
    } catch (error) {
      console.error('Erreur synchronisation badge:', error);
    }
  }

  /**
   * Supprime toutes les notifications de la barre de notification
   */
  async removeAllDeliveredNotifications(): Promise<void> {
    try {
      await PushNotifications.removeAllDeliveredNotifications();
    } catch (error) {
      console.error('Erreur suppression notifications:', error);
    }
  }

  /**
   * Vérifie si les notifications sont supportées
   */
  isSupported(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Récupère le token actuel
   */
  getToken(): string | null {
    return this.token || localStorage.getItem('push_token');
  }

  /**
   * Active les push sur cet appareil (initialisation + envoi éventuel du token en attente)
   */
  async enablePush(): Promise<void> {
    if (!this.isSupported()) return;
    await this.initialize();
    await this.sendPendingToken();
    // Badge potentiellement périmé depuis la dernière session (app relancée après
    // lecture des notifications ailleurs) : resynchronise dès l'activation.
    await this.syncBadge();
  }

  /**
   * Désactive les push sur cet appareil (suppression du token local et backend)
   */
  async disablePush(): Promise<void> {
    await this.removeToken();
  }
}

// Export singleton
export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;

